import React, { useEffect, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { PrintStatus } from "../../constants/PrintStatus";
import PrintingAnimation from "../PrintingAnimation";
import PrintingErrorAnimation from "../PrintingErrorAnimation";

export interface Props {
  status: PrintStatus;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const getElapsedMsSince = (start: Date) => {
  if (start === undefined) start = new Date();
  return new Date().getTime() - start.getTime();
};

const PrintAnimationWrapper: React.FC<Props> = ({ status }) => {
  let animationStartedTime = useRef<Date>();
  let nodeError = React.useRef(null);
  let nodeContent = React.useRef(null);

  const errorAnimationDuration = 5000;
  const printingAnimationDuration = 3000;
  const animationToErrorDelay = 1200;

  const [animationState, setAnimationState] = useState("none");
  const timeoutOptions = {
    enter: 500,
    exit: 300,
  };

  useEffect(() => {
    const triggerAnimation = async () => {
      switch (status) {
        case PrintStatus.PRINTING:
          animationStartedTime.current = new Date();
          setAnimationState("printing");
          break;

        case PrintStatus.ERROR:
          const msElapsed = getElapsedMsSince(animationStartedTime.current!);
          if (msElapsed < animationToErrorDelay)
            await wait(animationToErrorDelay - msElapsed);

          setAnimationState("error");
          await wait(errorAnimationDuration);
          setAnimationState("none");
          break;

        case PrintStatus.NONE:
          if (animationStartedTime.current) {
            const msElapsed = getElapsedMsSince(animationStartedTime.current!);
            if (msElapsed < printingAnimationDuration)
              await wait(printingAnimationDuration - msElapsed);

            setAnimationState("none");
            animationStartedTime.current = undefined;
          }
          break;
      }
    };

    triggerAnimation();
  }, [status]);

  const componentsToRender = [];

  switch (animationState) {
    case "error":
      componentsToRender.push({
        key: "dqm-animation-transition-error",
        ref: nodeError,
        component: <PrintingErrorAnimation reference={nodeError} />,
      });
      break;
    case "printing":
      componentsToRender.push({
        key: "dqm-animation-transition-printing",
        ref: nodeContent,
        component: <PrintingAnimation reference={nodeContent} />,
      });
      break;
  }

  return (
    <TransitionGroup component={null}>
      {componentsToRender.map((item) => (
        <CSSTransition
          key={item.key}
          appear={false}
          timeout={timeoutOptions}
          classNames="dqm-tcui-box-anim__opacity75"
          nodeRef={item.ref}
        >
          {item.component}
        </CSSTransition>
      ))}
    </TransitionGroup>
  );
};

export default PrintAnimationWrapper;
